import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Flame } from "lucide-react";

export default function DailyTasks({ ch, setCh }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => setData(await api.dailyTasks());
  useEffect(() => { refresh(); }, []);

  const claim = async (id) => {
    setBusy(true);
    try {
      const res = await api.dailyClaim(id);
      setCh(res.character);
      await refresh();
      toast.success(`+${res.gold}g · +${res.xp}xp · streak ${res.streak}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="panel p-4 text-center text-[#666] italic text-sm">Consulting the almanac...</div>;

  const streakMult = 1 + Math.min(1.0, (data.streak || 0) * 0.1);

  return (
    <div className="panel p-3 space-y-2" data-testid="daily-tasks">
      <div className="flex items-center justify-between">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">
          Daily Chronicle
        </div>
        <div className="flex items-center gap-1 font-mono text-xs" data-testid="daily-streak">
          <Flame className="w-3 h-3 text-[#E55A00] ember-pulse" strokeWidth={1.5} />
          <span className="text-[#E5C34A]">Streak {data.streak || 0}</span>
          <span className="text-[#666]">(×{streakMult.toFixed(1)})</span>
        </div>
      </div>
      {(data.tasks || []).map(t => {
        const pct = Math.min(100, (t.progress / t.target) * 100);
        const done = t.progress >= t.target;
        return (
          <div key={t.id} data-testid={`daily-${t.id}`} className="panel-inner p-2">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="font-heading text-xs text-[#E2DFD8]">{t.name}</div>
                <div className="font-mono text-[10px] text-[#8c8c8c] mt-0.5">
                  {t.progress}/{t.target} · +{Math.round(t.reward_gold * streakMult)}g · +{Math.round(t.reward_xp * streakMult)}xp
                </div>
                <div className="vbar mt-1" style={{ height: 4 }}>
                  <span style={{ width: `${pct}%`, background: done ? "#6E8B3D" : "#B89C3A" }} />
                </div>
              </div>
              <button
                data-testid={`daily-claim-${t.id}`}
                disabled={busy || !done || t.claimed}
                onClick={() => claim(t.id)}
                className={`ml-2 px-2 py-1 border font-heading text-[10px] tracking-widest uppercase ${
                  t.claimed ? "border-[#333] text-[#666]" : done ? "border-[#4A5D23] text-[#6E8B3D]" : "border-[#262626] text-[#666]"
                } disabled:opacity-40`}
              >
                {t.claimed ? <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> : "Claim"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
